// src/slices/workScheduleSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from '../store/store';
import { logout } from './authSlice';
import { apiUrl } from '../config/api';

export interface WorkSchedule {
  _id: string;
  userId: string;
  dayOfWeek: string;
  startTime?: string;
  endTime?: string;
  hours?: number;
  mode?: 'fixed' | 'flex';
  validFrom?: string;
  validTo?: string;
}

interface WorkScheduleState {
  schedules: WorkSchedule[];
  loading: boolean;
  error?: string;
}

const initialState: WorkScheduleState = {
  schedules: [],
  loading: false,
  error: undefined,
};

// 🔄 Alle Zeitpläne vom Server laden
export const fetchWorkSchedules = createAsyncThunk<
  WorkSchedule[],
  void,
  { state: RootState; dispatch: AppDispatch }
>('workSchedule/fetchAll', async (_, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl('/api/work-schedules'), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen');
  }
  if (!res.ok) throw new Error('Fehler beim Laden der Soll-Arbeitszeiten');
  return res.json();
});

// ➕ Neuer Zeitplan
export const createWorkSchedule = createAsyncThunk<
  WorkSchedule,
  {
    userId: string;
    dayOfWeek: string;
    startTime?: string | null;
    endTime?: string | null;
    hours?: number | null;
    mode?: string;
    validFrom?: string;
    validTo?: string | null;
  },
  { state: RootState; dispatch: AppDispatch }
>('workSchedule/create', async (data, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl('/api/work-schedules'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen');
  }
  if (!res.ok) throw new Error('Fehler beim Erstellen');
  return res.json();
});

// ✏️ Existierenden Zeitplan bearbeiten
export const editWorkSchedule = createAsyncThunk<
  WorkSchedule,
  {
    id: string;
    userId: string;
    dayOfWeek: string;
    startTime?: string | null;
    endTime?: string | null;
    hours?: number | null;
    mode?: string;
    validFrom?: string;
    validTo?: string | null;
  },
  { state: RootState; dispatch: AppDispatch }
>('workSchedule/edit', async ({ id, ...data }, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl(`/api/work-schedules/${id}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen');
  }
  if (!res.ok) throw new Error('Fehler beim Bearbeiten');
  return res.json();
});

// ❌ Zeitplan löschen
export const deleteWorkSchedule = createAsyncThunk<
  string,
  string,
  { state: RootState; dispatch: AppDispatch }
>('workSchedule/delete', async (id, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl(`/api/work-schedules/${id}`), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen');
  }
  if (!res.ok) throw new Error('Fehler beim Löschen');
  return id;
});

const workScheduleSlice = createSlice({
  name: 'workSchedule',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // Fetch
      .addCase(fetchWorkSchedules.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchWorkSchedules.fulfilled, (state, action) => {
        state.loading = false;
        state.schedules = action.payload;
      })
      .addCase(fetchWorkSchedules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Create
      .addCase(createWorkSchedule.fulfilled, (state, action) => {
        state.schedules.push(action.payload);
      })
      // Edit
      .addCase(editWorkSchedule.fulfilled, (state, action) => {
        const idx = state.schedules.findIndex(s => s._id === action.payload._id);
        if (idx !== -1) state.schedules[idx] = action.payload;
      })
      // Delete
      .addCase(deleteWorkSchedule.fulfilled, (state, action) => {
        state.schedules = state.schedules.filter(s => s._id !== action.payload);
      });
  },
});

export default workScheduleSlice.reducer;
